import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Modal,
  Row,
  Col,
} from "@themesberg/react-bootstrap";

import { adminGetPaymentConfirmList } from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const PAYMENT_METHOD_OPTIONS = [
  { value: "", label: "All Payment Methods" },
//   { value: "net banking", label: "Net Banking" },
//   { value: "wallet", label: "Wallet" },
  { value: "upi", label: "UPI" },
  { value: "bank transfer", label: "Bank Transfer" },
  { value: "lsg pay", label: "LSG Pay" },
];

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "N/A" || "-";
  const normalized = String(value).includes(" ")
    ? String(value).replace(" ", "T")
    : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export default function AdminPaymentConfirmList() {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [verifiedAmount, setVerifiedAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const initialSearchRef = useRef(true);
  const initialFilterRef = useRef(true);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchPaymentConfirmList = async (page = 1, searchTerm = "", method = "") => {
  try {
    setLoading(true);

    const params = { page };

    const trimmedSearch = String(searchTerm || "").trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (method) params.payment_method = method;

    const response = await adminGetPaymentConfirmList(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiTransactions = Array.isArray(apiData?.transactions)
        ? apiData.transactions
        : [];
      const apiPagination = apiData?.pagination || {};

      setTransactions(apiTransactions);
      setPagination({
        current_page: Number(apiPagination.current_page ?? page),
        last_page: Number(apiPagination.last_page ?? 1),
        per_page: Number(apiPagination.per_page ?? 10),
        total: Number(apiPagination.total ?? apiTransactions.length),
      });
    } catch (error) {
      console.error("Failed to fetch payment confirm list:", error);
      setTransactions([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
      toast.error(
        error?.response?.data?.message || "Unable to load payment confirm list."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentConfirmList(1, "", "");
  }, []);

  useEffect(() => {
    if (initialSearchRef.current) {
      initialSearchRef.current = false;
      return;
    }

    clearSearchDebounce();
    searchDebounceRef.current = setTimeout(() => {
      fetchPaymentConfirmList(1, search, paymentMethod);
    }, 300);

    return () => clearSearchDebounce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (initialFilterRef.current) {
      initialFilterRef.current = false;
      return;
    }

    fetchPaymentConfirmList(1, search, paymentMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  const openActionModal = (row, type) => {
    setSelectedRow(row);
    setActionType(type);
    setVerifiedAmount(row?.amount || "");
    setRemarks("");
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedRow(null);
    setActionType("");
    setVerifiedAmount("");
    setRemarks("");
  };

  const handleActionSubmit = () => {
    if (!verifiedAmount || Number(verifiedAmount) <= 0) {
      toast.error("Please enter verified amount.");
      return;
    }

    if (!selectedRow) {
      toast.error("Selected payment not found.");
      return;
    }

    if (actionType === "confirm") {
      toast.success(
        `Payment for Transaction ID ${selectedRow.transaction_id} confirmed.`
      );
    } else {
      toast.info(
        `Payment for Transaction ID ${selectedRow.transaction_id} rejected.`
      );
    }

    closeActionModal();
  };

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  return (
    <>
      <div className="payment-confirm-page">
        <div className="payment-confirm-header py-4">
          <div className="payment-confirm-header-top-row">
            {/* <h5 className="mb-0 payment-confirm-title">Admin Payment Confirm</h5> */}
             <h2 className="h4 mb-1 payment-confirm-title">Admin Payment Confirm</h2>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <div className="payment-confirm-filters-wrap mb-4">
              <Row className="g-3 align-items-end">
                <Col xs={12} md={6}>
                  <InputGroup className="payment-confirm-search">
                    <InputGroup.Text className="bg-white">
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ boxShadow: "none" }}
                    />
                  </InputGroup>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="payment-confirm-filter"
                    style={{ boxShadow: "none" }}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </div>

            {/* <div className="payment-confirm-list-top">
              <div>
                <h5 className="mb-1">Payment Confirm Details</h5>
                <small className="text-muted">Review and verify payment proof.</small>
              </div>
            </div> */}

            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Transaction ID</th>
                    <th>User</th>
                    <th className="text-end">Amount</th>
                    <th>Proof Upload</th>
                    <th>Date</th>
                    {/* <th className="text-center">Action</th> */}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Loading...
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => (
                      <tr key={item.transaction_id ?? index}>
                        <td className="fw-semibold">{start + index}</td>
                        <td>{item.transaction_id ?? "N/A"}</td>
                        <td>{item.user_name ?? "N/A"}</td>
                        <td className="text-end">{formatCurrency(item.amount)}</td>
                        <td>
                          {item.proof_upload ? (
                            <Button
                              type="button"
                              variant="outline-primary"
                              size="sm"
                              className="fw-semibold"
                              onClick={() => window.open(item.proof_upload, "_blank")}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-2" />
                              View
                            </Button>
                          ) : (
                            "N/A" || "-"
                          )}
                        </td>
                        <td>{formatDate(item.date)}</td>
                        {/* <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            <Button
                              type="button"
                              className="fw-semibold"
                              style={ACTIVE_BUTTON_STYLE}
                              onClick={() => openActionModal(item, "confirm")}
                            >
                              <FontAwesomeIcon icon={faCheckCircle} />
                              Confirm
                            </Button>
                            <Button
                              type="button"
                              variant="outline-danger"
                              className="fw-semibold"
                              onClick={() => openActionModal(item, "reject")}
                            >
                              <FontAwesomeIcon icon={faTimesCircle} />
                              Reject
                            </Button>
                          </div>
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No payment confirm records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {start} to {end} of {pagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() => {
                    clearSearchDebounce();
                    fetchPaymentConfirmList(
                      Math.max(1, pagination.current_page - 1),
                      search,
                      paymentMethod
                    );
                  }}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {getPageItems().map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() => {
                        clearSearchDebounce();
                        fetchPaymentConfirmList(item, search, paymentMethod);
                      }}
                      className={`course-page-btn ${
                        item === pagination.current_page
                          ? "course-page-btn-active"
                          : ""
                      }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page >= pagination.last_page || loading}
                  onClick={() => {
                    clearSearchDebounce();
                    fetchPaymentConfirmList(
                      Math.min(pagination.last_page, pagination.current_page + 1),
                      search,
                      paymentMethod
                    );
                  }}
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showActionModal} onHide={closeActionModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">
            {actionType === "confirm" ? "Confirm Payment" : "Reject Payment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted small mb-1">Transaction ID</div>
            <div className="fw-semibold">{selectedRow?.transaction_id ?? "N/A"}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Verified Amount <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={verifiedAmount}
              onChange={(e) => setVerifiedAmount(e.target.value)}
              placeholder="Enter verified amount"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold">Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add note"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeActionModal}>
            Cancel
          </Button>
          <Button
            style={actionType === "confirm" ? ACTIVE_BUTTON_STYLE : {}}
            variant={actionType === "confirm" ? "primary" : "danger"}
            onClick={handleActionSubmit}
          >
            {actionType === "confirm" ? "Confirm" : "Reject"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .payment-confirm-page {
          width: 100%;
          padding-inline: 0;
        }

        .payment-confirm-header {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }

        .payment-confirm-header-top-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

    
        .payment-confirm-search {
          width: 100%;
        }

        .payment-confirm-filter {
          width: 100%;
          margin-left: auto;
        }

        .payment-confirm-filters-wrap {
          width: 100%;
        }

        .payment-confirm-list-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 12px;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 992px) {
          .payment-confirm-search,
          .payment-confirm-filter {
            max-width: 100%;
          }
        }

        @media (max-width: 767.98px) {
          .payment-confirm-header-top-row {
            align-items: flex-start;
          }

          .payment-confirm-list-top {
            flex-direction: column;
            align-items: stretch;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }

          .payment-confirm-search,
          .payment-confirm-filter {
            max-width: 100%;
          }
        }
      `}</style>
    </>
  );
}